/**
 * PipelineStepSettings — slide-over panel for editing a single pipeline
 * step or pipeline-level settings.
 *
 * Store-connected: reads from useEditorStore, usePreferencesStore, usePresetStore.
 * Pipeline-level settings are delegated to PipelineSettingsPanel.
 */

import { Trash2 } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AttributeInput } from '@/components/common/AttributeInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToolCatalog } from '@/hooks/useToolCatalog';
import type { IToolPreset } from '@/interfaces/IStorage';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { usePreferencesStore } from '@/store/usePreferencesStore';
import { usePresetStore } from '@/store/usePresetStore';

import { PipelineSettingsPanel } from './PipelineSettingsPanel';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PipelineStepSettingsProps {
  stepId: string | null;
  pipelineSettingsOpen: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PipelineStepSettings: React.FC<PipelineStepSettingsProps> = ({
  stepId,
  pipelineSettingsOpen,
}) => {
  const { t } = useTranslation(['settings', 'pipeline']);
  const catalog = useToolCatalog();
  const { updateToolConfig } = useAppStore();
  const pipeline = useEditorStore((s) => s.pipeline);
  const setStepLabel = useEditorStore((s) => s.setStepLabel);
  const showToolNameWithStepLabel = usePreferencesStore((s) => s.showToolNameWithStepLabel);
  const activePipelineName = useEditorStore((s) => s.activePipelineName);
  const activePipelineDescription = useEditorStore((s) => s.activePipelineDescription);

  const presets = usePresetStore((s) => s.presets);
  const createPreset = usePresetStore((s) => s.createPreset);
  const updatePreset = usePresetStore((s) => s.updatePreset);
  const deletePreset = usePresetStore((s) => s.deletePreset);

  const step = stepId ? pipeline.find((p) => p.id === stepId) : undefined;
  const tool = step ? catalog.getToolById(step.toolId) ?? null : null;

  const presetsForTool = React.useMemo<IToolPreset[]>(() => {
    if (!tool) return [];
    return presets.filter((p) => p.toolId === tool.id).slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [presets, tool]);

  const [selectedStepPresetId, setSelectedStepPresetId] = React.useState<string>('');
  const selectedStepPreset = React.useMemo(
    () => (selectedStepPresetId ? presetsForTool.find((p) => p.id === selectedStepPresetId) ?? null : null),
    [presetsForTool, selectedStepPresetId],
  );

  const [stepPresetName, setStepPresetName] = React.useState('');

  React.useEffect(() => { setSelectedStepPresetId(''); setStepPresetName(''); }, [stepId]);
  React.useEffect(() => { if (selectedStepPreset) setStepPresetName(selectedStepPreset.name); }, [selectedStepPreset?.id]);

  if (!pipelineSettingsOpen && (!step || !tool)) return null;

  const stepLabel = step?.label?.trim();
  const stepTitle = stepLabel
    ? showToolNameWithStepLabel ? `${stepLabel} · ${tool?.name ?? ''}` : stepLabel
    : tool?.name;

  const title = pipelineSettingsOpen ? t('settings:pipelineSettings.title') : stepTitle;
  const subtitle = pipelineSettingsOpen ? (activePipelineName || t('pipeline:title')) : (tool?.category ?? 'Tool');
  const description = pipelineSettingsOpen ? activePipelineDescription : tool?.description;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Info header */}
      <div className="border-b border-border px-6 py-3 shrink-0">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
          <div className="text-base font-semibold truncate">{title}</div>
          {description && <div className="text-xs text-muted-foreground mt-1 truncate">{description}</div>}
        </div>
      </div>

      <div className="p-6 overflow-auto flex-1 min-h-0">
        {pipelineSettingsOpen ? (
          <PipelineSettingsPanel />
        ) : (
          <div className="space-y-6">
            {/* Step name */}
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('settings:pipelineSettings.stepName')}</div>
              <Input value={step!.label ?? ''} onChange={(e) => setStepLabel(step!.id, e.target.value)}
                placeholder={tool?.name ?? t('settings:pipelineSettings.stepName')} className="h-8" />
            </div>

            {/* Presets */}
            <div className="space-y-3">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('settings:pipelineSettings.presets')}</div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-48">
                  <Select value={selectedStepPresetId || "__none__"}
                    onValueChange={(value) => {
                      const val = value === "__none__" ? "" : value;
                      setSelectedStepPresetId(val);
                      setStepPresetName(presetsForTool.find((p) => p.id === val)?.name ?? '');
                    }}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder={t('settings:pipelineSettings.selectPreset')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('common:labels.none')}</SelectItem>
                      {presetsForTool.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-48">
                  <Input value={stepPresetName} onChange={(e) => setStepPresetName(e.target.value)}
                    placeholder={selectedStepPreset ? t('settings:pipelineSettings.presetName') : t('settings:pipelineSettings.newPresetName')} className="h-8" />
                </div>

                {selectedStepPreset && (
                  <Button variant="secondary" size="sm"
                    onClick={() => updateToolConfig(step!.id, { ...(selectedStepPreset.config ?? {}) })}
                    title={t('common:actions.apply')}>
                    {t('common:actions.apply')}
                  </Button>
                )}

                <Button variant="secondary" size="sm" onClick={() => {
                  if (!tool) return;
                  const created = createPreset({ toolId: tool.id, name: stepPresetName, config: step!.config });
                  setSelectedStepPresetId(created.id);
                  setStepPresetName(created.name);
                }} title={t('settings:pipelineSettings.createPreset')}>
                  {t('settings:pipelineSettings.createPreset')}
                </Button>

                {selectedStepPreset && (
                  <>
                    <Button variant="secondary" size="sm"
                      onClick={() => updatePreset(selectedStepPreset.id, { name: stepPresetName, config: step!.config })}
                      title={t('settings:pipelineSettings.updatePreset')}>
                      {t('settings:pipelineSettings.updatePreset')}
                    </Button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 px-0"
                            onClick={() => { deletePreset(selectedStepPreset.id); setSelectedStepPresetId(''); setStepPresetName(''); }}
                            aria-label={t('settings:pipelineSettings.deletePreset')}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{t('settings:pipelineSettings.deletePreset')}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </>
                )}
              </div>
            </div>

            {/* Configuration */}
            <div className="space-y-3">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('settings:pipelineSettings.configuration')}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(tool?.attributes ?? []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">{t('settings:pipelineSettings.noSettings')}</div>
                ) : (
                  (tool?.attributes ?? []).map((attr) => (
                    <div key={attr.name} className="space-y-1.5">
                      <label className="text-xs font-medium text-muted-foreground">{attr.label}</label>
                      <AttributeInput attr={attr} value={step!.config[attr.name]} compact
                        onChange={(name, value) => updateToolConfig(step!.id, { ...step!.config, [name]: value })} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


